import React, { useEffect, useRef, useState } from 'react'
import HintContent from './HintContent'
import MaskedAnswer from './MaskedAnswer'

// Pronunciation prompt. Same input/feedback UX as VocabularyPrompt, but
// the prompt is the target word and the answer is its respell (e.g.
// "kohld" for "cold"). A speaker icon becomes clickable after the kid
// reveals the answer or submits, and auto-plays audio the first time it
// appears — so the kid always hears the correct pronunciation once.
export default function PronunciationPrompt({
  item,
  totalScore,
  targetScore,
  onSubmit,
  onUseHint,
  onNext,
  submitting
}) {
  const [answer, setAnswer] = useState('')
  const [feedback, setFeedback] = useState(null)
  const [revealed, setRevealed] = useState(null)
  const [hintUsed, setHintUsed] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const inputRef = useRef(null)
  const audioRef = useRef(null)
  const autoPlayedRef = useRef(false)

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus()
  }, [])

  const showSpeaker = hintUsed || submitted

  // Auto-play the audio the first time the speaker icon appears in this
  // attempt, so the kid always hears the correct pronunciation.
  useEffect(() => {
    if (!showSpeaker || autoPlayedRef.current) return
    autoPlayedRef.current = true
    playAudio()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSpeaker])

  const playAudio = () => {
    const audio = audioRef.current
    if (!audio) return
    try {
      audio.currentTime = 0
      const p = audio.play()
      if (p && typeof p.catch === 'function') p.catch(() => {})
    } catch {
      // ignore playback failures - browser may block until user gesture.
    }
  }

  const handleSubmit = async (event) => {
    if (event && event.preventDefault) event.preventDefault()
    if (submitting || !answer.trim()) return

    try {
      const result = await onSubmit(answer)
      setSubmitted(true)
      if (result.correct) {
        setFeedback({ correct: true, message: 'Chính xác! 🎉' })
      } else {
        setFeedback({ correct: false, message: 'Chưa đúng, hãy thử lại.' })
      }
    } catch (err) {
      setFeedback({ correct: false, message: 'Có lỗi khi gửi đáp án. Thử lại nhé.' })
    }
  }

  const handleHint = async () => {
    if (submitting) return
    try {
      const result = await onUseHint()
      if (result?.answer) setRevealed(result.answer)
      setHintUsed(true)
    } catch (err) {
      setFeedback({ correct: false, message: 'Không thể lấy gợi ý.' })
    }
  }

  const done = feedback?.correct || hintUsed
  const disabled = submitting || done
  const showInputBadge = answer.length <= 3
  const attemptNumber = Math.min(totalScore + 1, targetScore)

  return (
    <div className="vocabulary-card">
      <div className="vocabulary-progress">
        <span>Câu {attemptNumber} / {targetScore}</span>
        <span>·</span>
        <span>Điểm: <strong>{totalScore}</strong> / {targetScore}</span>
      </div>

      <HintContent hint={`Phiên âm của '${item.word}'`} />

      <div className="vocabulary-bar-wrapper">
        <div className="vocabulary-masked" aria-label="pronunciation preview">
          <MaskedAnswer
            masked={item.masked_answer}
            answer={item.answer || ''}
            input={answer}
          />
        </div>
        <span className="vocabulary-badge hint-badge" data-testid="pronunciation-hint-badge">
          Gợi ý
        </span>
      </div>

      {revealed && (
        <div className="vocabulary-hint-reveal">Đáp án: {revealed}</div>
      )}

      {!hintUsed && (
        <form
          onSubmit={handleSubmit}
          className={`vocabulary-bar-wrapper${showInputBadge ? ' with-badge' : ''}`}
        >
          <input
            ref={inputRef}
            type="text"
            className="vocabulary-input"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            disabled={disabled}
            data-testid="pronunciation-answer-input"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
          />
          {showInputBadge && (
            <span
              className="vocabulary-badge answer-badge"
              data-testid="pronunciation-answer-badge"
            >
              Bạn trả lời
            </span>
          )}
        </form>
      )}

      {feedback && (
        <div className={`vocabulary-feedback ${feedback.correct ? 'success' : 'error'}`}>
          {feedback.message}
        </div>
      )}

      {showSpeaker && item.audio_url && (
        <audio
          ref={audioRef}
          src={item.audio_url}
          preload="auto"
          data-testid="pronunciation-audio"
        />
      )}

      <div className="vocabulary-actions">
        {!hintUsed && (
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={handleHint}
            disabled={submitting || feedback?.correct}
            data-testid="pronunciation-hint-button"
          >
            💡 Xem đáp án
          </button>
        )}
        {showSpeaker && (
          <button
            type="button"
            className="btn btn-outline-primary pronunciation-speaker"
            onClick={playAudio}
            disabled={!item.audio_url}
            data-testid="pronunciation-speaker-button"
            aria-label="Nghe cách đọc"
          >
            🔊
          </button>
        )}
        {done ? (
          <button
            type="button"
            className="btn btn-primary ms-auto"
            onClick={onNext}
            data-testid="pronunciation-next-button"
          >
            Tiếp theo →
          </button>
        ) : (
          <button
            type="submit"
            className="btn btn-primary"
            onClick={handleSubmit}
            disabled={disabled || !answer.trim()}
            data-testid="pronunciation-submit-button"
          >
            Kiểm tra
          </button>
        )}
      </div>
    </div>
  )
}
